import {
  createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useOverlayTransition } from '../../hooks/ui/useOverlayTransition';
import { scrimCoversBottomBar, useSuppressAiFab } from '../../contexts/AiFabSuppressionContext';
import { animatedModalShellPropTypes } from './animatedModalShellPropTypes';
import { useVisualViewportFrame } from '../../hooks/ui/useVisualViewportFrame';
import { useBodyScrollLock } from '../../utils/ui/bodyScrollLock';
import { modalScrimCentered } from '../../ui/overlayStyles';
import { withDefaultPanelCap } from '../../ui/modalPanelCap';
import { composeDialogTransition, useDialogSteady } from './dialogKeyboardTransition';
import { bulkBackgroundMorphDurationMs, computeFlipInvertTransform, runFlipSpringMorph } from '../../ui/morphMotion';
import useKeyboardFieldReveal, { useKbDebugRectSampler } from '../../hooks/ui/useKeyboardFieldReveal';
import useModalKeyboardMode from '../../hooks/ui/useModalKeyboardMode';
import useModalKeyboardAnticipation from '../../hooks/ui/useModalKeyboardAnticipation';
import useModalTypingRest from '../../hooks/ui/useModalTypingRest';
import useReducedMotion from '../../hooks/ai/useReducedMotion';
import {
  modalPanelMotionStyle,
  modalScrimMotionStyle,
  prefersCoarsePointer,
} from '../../ui/overlayMotion';
import { OVERLAY_MOTION } from '../../ui/motion/overlayMotionTokens';

/**
 * Shape-based keyboard handling (founder decision, 2026-08-20): the shell
 * measures the dialog's resting geometry and picks ONE of two native-feeling
 * modes automatically — `'glide'` (small dialog translates up once, synced
 * to the keyboard curve) or `'curtain'` (tall dialog pins its top edge and
 * clamps its bottom to the keyboard line, animated on the same curve so the
 * edge rides the slide instead of snapping). See `keyboardOverlayStyles.js`
 * for the pure math and `useModalKeyboardMode.js` (the resting-rect capture,
 * close-linger, and curtain two-phase start that decide WHEN each style
 * applies) for the full "why" — it replaces an even earlier instant-clamp
 * build the founder rejected on device ("modal shrinks") for applying while
 * the keyboard was still sliding.
 */

/** When false, defer heavy body layout until the enter transition has started. */
export const ModalContentReadyContext = createContext(true);

export function useModalContentReady() {
  return useContext(ModalContentReadyContext);
}

/**
 * Centered modal scrim + panel with coordinated enter/exit animation.
 * Keeps mounted through the exit transition so close feels smooth on mobile.
 *
 * Parent should keep rendering while `isOpen` is true; set `isOpen={false}`
 * (or call `onRequestClose` which toggles internal state) and listen on
 * `onExitComplete` to clear parent state / unmount.
 */
export default function AnimatedModalShell({
  isOpen,
  onExitComplete,
  onEnterMorphComplete,
  onScrimClick,
  onScrimMouseDown,
  zIndex,
  dialogStyle,
  dialogRef,
  scrimStyle,
  scrimProps = {},
  deferMobileBody = true,
  /**
   * `'auto'` slide-up on touch / scale on desktop; `'scale'` always uses desktop
   * scale-in; `'fade'` plain opacity fade (no scale, no slide) for a calm,
   * nothing-fancy entrance; `'pop'` a quick centred grow-in (sign-out confirm).
   */
  panelMotion = 'auto',
  /** Scrim opacity (0–1) the ENTER starts from — continues a dim already on screen (menu → confirm hand-off). */
  scrimEnterFromOpacity = 0,
  /** Opt-in corner-morph: when true and exiting, use `morphExitStyle` instead of the default panel exit. */
  morphActive = false,
  morphExitStyle = null,
  /**
   * Opt-in REVERSE morph (pill → modal). When a rect is supplied on open, the
   * live dialog panel FLIP-springs from that rect to its resting size — content
   * rides inside the same box (no decoy overlay). null = normal entrance.
   */
  morphEnterFromRect = null,
  /** Apply the shape-based keyboard handling (glide/curtain) when the mobile keyboard opens. */
  keyboardAware = true,
  /**
   * Where a GLIDED dialog (one that fits above the keyboard) settles in the
   * visible band: `'center'` (default — every app modal) or `'end'` to hug
   * the keyboard (login cards — a short card centered in the band left a
   * dead gap above the keys and read as floating too high). No effect on a
   * CURTAIN dialog (tall — its top edge always pins, never repositions).
   */
  keyboardAlign = 'center',
  /** KAN-1181: false = a touch card never rests on a typing-field tap (pre-sign-in cards). true/omitted = the tap decides (no extra effect). */
  typingRest,
  /** Skip the enter animation — panel appears instantly at full opacity. */
  skipEnter = false,
  children,
  dialogProps = {},
}) {
  const preferMobile = useMemo(() => prefersCoarsePointer(), []);
  const reducedMotion = useReducedMotion();
  const scaleMotion = panelMotion === 'scale';
  const fadeMotion = panelMotion === 'fade';
  // ONE pop-up clock for every panelMotion (RD-UI-192): 220ms in, 200ms out.
  const durationMs = OVERLAY_MOTION.popEnterMs;
  const morphDurationMs = useMemo(() => bulkBackgroundMorphDurationMs(preferMobile), [preferMobile]);
  const overlayDurationMs = (!isOpen && morphActive) ? morphDurationMs : OVERLAY_MOTION.popExitMs;
  const { mounted, visible } = useOverlayTransition(isOpen, overlayDurationMs, skipEnter);
  // Every pop-up hides the floating AI button while it is on screen, so the
  // button can never sit over (and steal taps from) a dialog's actions (KAN-786).
  // A docked button stays (dimmed with the bar) only when this scrim is above the
  // bar; a pop-up without a z stays a hard hide (KAN-1011).
  useSuppressAiFab(mounted, { keepDocked: scrimCoversBottomBar(zIndex) });
  const [contentReady, setContentReady] = useState(false);
  const [dialogEl, setDialogEl] = useState(null);
  // Scope the keyboard reframe to THIS dialog's subtree (pass dialogEl): only
  // reframe when the focused editable lives inside this modal, never when a
  // background field merely kept focus on the iOS PWA (the "mark as paid"
  // confirm that pinned itself under the status bar).
  const viewportFrame = useVisualViewportFrame(
    keyboardAware && preferMobile && mounted && visible,
    dialogEl,
  );
  const dialogFlags = useDialogSteady(visible, durationMs, preferMobile, viewportFrame.keyboardOpen);

  const setDialogNode = useCallback((node) => {
    setDialogEl(node);
    if (typeof dialogRef === 'function') dialogRef(node);
    else if (dialogRef && typeof dialogRef === 'object') dialogRef.current = node;
  }, [dialogRef]);
  // KAN-1181 tap-rest: opens centred; a tap on a typing field glides the card up (useModalTypingRest).
  const typing = useModalTypingRest({
    dialogEl, mounted, preferMobile, typingRest, keyboardAware, keyboardOpen: viewportFrame.keyboardOpen, visible,
  }); // …and the action beat sends it back to the centre (useModalRestRelease)
  const [enterDestRect, setEnterDestRect] = useState(null);
  const [enterDone, setEnterDone] = useState(false);
  const [enterSettled, setEnterSettled] = useState(false);
  const enterSpringStartedRef = useRef(false);
  const enterMorphCompleteRef = useRef(onEnterMorphComplete);
  enterMorphCompleteRef.current = onEnterMorphComplete;

  const enterMorphActive = isOpen && !!morphEnterFromRect && !enterDone;
  const morphClockActive = morphActive || enterMorphActive;

  useLayoutEffect(() => {
    if (!enterMorphActive || !dialogEl || enterDestRect) return;
    const r = dialogEl.getBoundingClientRect();
    setEnterDestRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [enterMorphActive, dialogEl, enterDestRect]);

  // FLIP the live dialog (content inside) from pill → resting rect.
  useLayoutEffect(() => {
    if (!enterMorphActive || !enterDestRect || !morphEnterFromRect || !dialogEl) {
      return undefined;
    }

    if (reducedMotion) {
      setEnterDone(true);
      enterMorphCompleteRef.current?.();
      return undefined;
    }

    let stopped = false;
    let stopAnim = () => {};
    let rafId = 0;

    const startSpring = () => {
      if (stopped || enterSpringStartedRef.current || !dialogEl) return false;
      enterSpringStartedRef.current = true;
      stopAnim = runFlipSpringMorph(
        dialogEl,
        morphEnterFromRect,
        enterDestRect,
        {
          isTouch: preferMobile,
          reduced: false,
          onComplete: () => {
            if (stopped) return;
            setEnterDone(true);
            enterMorphCompleteRef.current?.();
          },
        },
      );
      return true;
    };

    const waitAndStart = () => {
      if (stopped) return;
      if (startSpring()) return;
      rafId = requestAnimationFrame(waitAndStart);
    };

    const invert = computeFlipInvertTransform(morphEnterFromRect, enterDestRect);
    dialogEl.style.transformOrigin = '0 0';
    dialogEl.style.transform = invert;
    dialogEl.style.willChange = 'transform';

    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(waitAndStart);
    });

    const failSafe = setTimeout(() => {
      if (stopped || enterDone) return;
      enterSpringStartedRef.current = true;
      setEnterDone(true);
      enterMorphCompleteRef.current?.();
    }, morphDurationMs + 120);

    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      clearTimeout(failSafe);
      stopAnim();
      enterSpringStartedRef.current = false;
    };
  }, [
    enterMorphActive, enterDestRect, morphEnterFromRect, dialogEl,
    enterDone, preferMobile, reducedMotion, morphDurationMs,
  ]);

  useLayoutEffect(() => {
    if (!enterDone || !dialogEl) return;
    dialogEl.style.transform = '';
    dialogEl.style.transformOrigin = '';
    dialogEl.style.willChange = '';
  }, [enterDone, dialogEl]);

  useLayoutEffect(() => {
    if (!enterDone || enterSettled) return undefined;
    const raf = requestAnimationFrame(() => setEnterSettled(true));
    return () => cancelAnimationFrame(raf);
  }, [enterDone, enterSettled]);

  useLayoutEffect(() => {
    if (isOpen) return;
    setEnterDestRect(null);
    setEnterDone(false);
    setEnterSettled(false);
    enterSpringStartedRef.current = false;
  }, [isOpen]);

  // Hoisted above the useModalKeyboardMode call below (which depends on it)
  // — still used again at render time for effectivePanelStyle.
  const enterMorphSettling = !!morphEnterFromRect && isOpen && !enterSettled;

  // Owns the resting-rect capture, close-linger, glide/curtain computation,
  // and the curtain two-phase start — extracted to useModalKeyboardMode.js
  // (500-line cap; see its header for the full "why", including the P1 fix
  // for the founder-rejected instant `max-height` snap).
  const { keyboardMode, keyboardScrimStyle, keyboardDialogStyle, curtainPhase1 } = useModalKeyboardMode({
    viewportFrame, dialogEl, keyboardAlign, enterMorphActive, enterMorphSettling, reducedMotion, rest: typing.rest && !typing.fallback,
  });

  // Focus-time pan PREVENTION for fields the inline guard cannot reveal
  // (no usable scroller / hug-content panel): re-anchor the dialog above the
  // predicted centring line before iOS samples the field. Geometry-
  // self-coordinating with the guard — see useModalKeyboardAnticipation.
  const { scrimStyle: anticipationScrimStyle, isDismissSuppressed } = useModalKeyboardAnticipation({
    dialogEl, keyboardOpen: viewportFrame.keyboardOpen, enterMorphActive, visible, onRestFallback: typing.setFallback, suppressBridge: typing.suppressBridge,
    tapRest: typing.tapRestEnabled, onTapRest: typing.onTapRest, restReleased: typing.released,
  });

  // STRUCTURAL DISMISS GUARD (founder report 2026-08-21: tapping the typed-
  // confirm FIELD closed the delete modal). A click's target is assembled
  // from its press and release nodes; if layout moves between them (the
  // anticipation anchor at focus, the release glide, a curtain reframe), a
  // tap that BEGAN inside the dialog can resolve against the scrim. So the
  // scrim only dismisses when the PRESS itself began on the scrim —
  // recorded at pointerdown, which fires at true press time, before any
  // focus-driven layout motion (iOS compat mousedown fires late, after the
  // move, and would record the wrong node). Per-caller mousedown tracking
  // (ConfirmDialog) stays as a second layer but the shell is the authority,
  // so every modal — including ones that never added their own check — is
  // covered.
  const scrimPressOnScrimRef = useRef(false);
  const recordScrimPress = (e) => {
    scrimPressOnScrimRef.current = e.target === e.currentTarget;
  };

  useBodyScrollLock(mounted && visible);

  // Reveal-the-focused-field choreography + restore-on-close, and the kbdebug
  // rect sampler — extracted to useKeyboardFieldReveal.js (500-line cap; see
  // its header for the three device-log iterations that shaped the rules).
  useKeyboardFieldReveal(viewportFrame.keyboardOpen);
  useKbDebugRectSampler(viewportFrame.keyboardOpen, dialogEl);

  useLayoutEffect(() => {
    if (!mounted || !visible) {
      setContentReady(false);
      return undefined;
    }
    if (!deferMobileBody || scaleMotion || fadeMotion || !preferMobile) {
      setContentReady(true);
      return undefined;
    }
    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      if (!cancelled) setContentReady(true);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [mounted, visible, deferMobileBody, preferMobile, scaleMotion, fadeMotion]);

  useLayoutEffect(() => {
    if (mounted || isOpen) return undefined;
    onExitComplete?.();
  }, [mounted, isOpen, onExitComplete]);

  if (!mounted) return null;
  if (typeof document === 'undefined') return null;

  const baseScrimMotion = modalScrimMotionStyle(
    visible, morphClockActive ? morphDurationMs : (visible ? OVERLAY_MOTION.popEnterMs : OVERLAY_MOTION.popExitMs),
  );
  const scrimMotion = (!visible && isOpen && scrimEnterFromOpacity > 0)
    ? { ...baseScrimMotion, opacity: scrimEnterFromOpacity } : baseScrimMotion;
  const panelMotionStyle = modalPanelMotionStyle(visible, durationMs, preferMobile, scaleMotion, fadeMotion);

  let effectivePanelStyle;
  if (morphActive && !isOpen && morphExitStyle) {
    effectivePanelStyle = {
      opacity: 0,
      visibility: 'hidden',
      transition: 'none',
      pointerEvents: 'none',
    };
  } else if (enterMorphActive || enterMorphSettling) {
    // Real panel morphs — visible throughout; transform owned by FLIP spring.
    effectivePanelStyle = {
      opacity: enterDestRect ? 1 : 0,
      transition: 'none',
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden',
    };
  } else if (enterDone && !enterSettled) {
    effectivePanelStyle = { opacity: 1, transform: 'none', transition: 'none' };
  } else {
    effectivePanelStyle = panelMotionStyle;
  }

  // Glide's `transform` must apply AFTER effectivePanelStyle: the panel style
  // owns `transform` during enter-slide / FLIP-morph, and glide must not
  // fight it mid-animation — so glide is skipped during exactly the same two
  // phases effectivePanelStyle branches on above (exit-morph, enter-morph/
  // -settling). Once settled, glide takes over `transform` for the
  // keyboard-open translate, on the iOS keyboard curve; curtain mode never
  // touches `transform` so it applies earlier, alongside dialogStyle.
  // Also requires `visible`: during exit (isOpen false, still mounted for the
  // exit transition) the panel's own exit transform must win — glide
  // overwriting it for the close-linger window read as a fade-in-place
  // instead of the dialog's real exit motion.
  const glideActive = keyboardMode === 'glide'
    && visible
    && !(morphActive && !isOpen && morphExitStyle)
    && !(enterMorphActive || enterMorphSettling);
  const glideDialogStyle = glideActive ? keyboardDialogStyle : null;
  const dialogTransition = composeDialogTransition({
    preferMobile, reducedMotion, panelT: effectivePanelStyle.transition, ...dialogFlags, visible, phase1: curtainPhase1,
  }); // single owner of the dialog transition (keyboard list first, panel last)
  return createPortal(
    <ModalContentReadyContext.Provider value={contentReady}>
      <div
        style={{
          ...modalScrimCentered,
          zIndex,
          ...scrimMotion,
          // Spread order is load-bearing: ...keyboardScrimStyle AFTER
          // ...scrimStyle, so caller styles can never clobber the curtain
          // pin while the keyboard is up (the 2026-07-31 lesson in CLAUDE.md
          // — a caller-supplied style must not win over an active keyboard
          // guard — applies here the same way). null in glide mode (its
          // scrim is unchanged), so this is a no-op there.
          ...scrimStyle,
          // Anticipation sits between the caller's scrim style and the
          // curtain pin: it must beat a caller's centered layout, and the
          // curtain (measured AFTER anticipation, so they agree) still wins.
          ...anticipationScrimStyle,
          ...keyboardScrimStyle,
        }}
        onPointerDown={recordScrimPress}
        onMouseDown={(e) => {
          // Antique-browser fallback: no PointerEvent → mousedown is the
          // earliest press signal available.
          if (typeof window.PointerEvent === 'undefined') recordScrimPress(e);
          if (onScrimMouseDown) onScrimMouseDown(e);
        }}
        onClick={(e) => {
          if (!scrimPressOnScrimRef.current) return; // press began inside the dialog
          if (isDismissSuppressed()) return; // card is (or just was) in motion
          if (onScrimClick) onScrimClick(e);
        }}
        role="presentation"
        {...scrimProps}
      >
        <div
          ref={setDialogNode} data-cvx-modal-panel="" data-cvx-raised="" data-cvx-floating=""
          data-cvx-kb-rest={typing.rest ? '' : undefined}
          // NOT exempt from the global inline keyboard guard (the
          // `data-cvx-kb-guard-exempt` opt-out was here for a few hours on
          // 2026-08-20 and removed the same day). The exemption assumed modals
          // "own their keyboard reveal" — but useKeyboardFieldReveal fires at
          // REVEAL_DELAY_MS (250ms), and the device log shows iOS pans the
          // whole viewport at ~+101ms and has un-panned by ~+200ms. The modal's
          // own reveal is CORRECTIVE and structurally cannot prevent the pan;
          // the founder's add-client drawer capture panned 59px with this
          // exemption in place. Prevention has to be synchronous at focusin,
          // which is exactly what the guard does.
          //
          // The two compose safely rather than double-fighting: if the guard
          // already raised the field, useKeyboardFieldReveal's own
          // `isRevealNeeded` check finds it visible, logs `skip:1`, and records
          // NO restore — so there is only ever one scroll and one restore.
          style={{
            ...withDefaultPanelCap(dialogStyle),
            // Curtain never touches `transform`, so it's safe to apply here,
            // before effectivePanelStyle. Glide's transform is applied LATER
            // (see glideDialogStyle below) — never here.
            ...(keyboardMode === 'curtain' ? keyboardDialogStyle : null),
            ...effectivePanelStyle,
            ...glideDialogStyle,
            ...(dialogTransition ? { transition: dialogTransition } : null),
          }}
          onClick={(e) => e.stopPropagation()}
          // *** DEFAULTS, NOT OVERRIDES — `{...dialogProps}` comes AFTER, so any
          // caller that already declares its own role (51 do, including
          // ConfirmDialog, ConfirmDialogInline and DeclineEstimateModal) keeps
          // it, and a caller wanting `alertdialog` can still say so. ***
          //
          // WHY IT MATTERS BEYOND a11y: `resolveFilterSelectContainRect`
          // (computeFilterSelectPlacement.js) finds a popover's containing panel
          // via `triggerEl.closest('[role="dialog"]')`. A modal without the
          // attribute resolves NULL, so a FilterSelect inside it measures its
          // space against the whole VIEWPORT, sees room above the card, and
          // flips UP over its own modal's chrome — or spills onto the scrim.
          // The shells that were missing it are exactly the ones nobody thought
          // to hand-wire (MfaEnrollmentGateShell, BulkStatusPickerModal,
          // BulkLifecycleReviewModal, PeriodSummaryModal,
          // ReportPreview, InvoicePreview, EstimatePreview) — the same
          // silent-wiring class as the admin re-list chain, which is why the
          // default belongs in the shell and not on a per-caller key-list.
          role="dialog"
          aria-modal="true"
          {...dialogProps}
        >
          {children}
        </div>
      </div>
    </ModalContentReadyContext.Provider>,
    document.body,
  );
}

AnimatedModalShell.propTypes = animatedModalShellPropTypes;

export function DeferredModalBody({ children, placeholderMinHeight = 120, style }) {
  const contentReady = useModalContentReady();
  if (!contentReady) {
    return <div style={{ ...style, minHeight: placeholderMinHeight }} aria-hidden />;
  }
  return <div style={style}>{children}</div>;
}

DeferredModalBody.propTypes = {
  children: PropTypes.node,
  placeholderMinHeight: PropTypes.number,
  style: PropTypes.object,
};
